import React, { useState, useEffect, useCallback } from "react";
import { Plus, Download, Upload } from "lucide-react";
import { toast } from '../utils/toast';

import FirmList from "../components/firms/FirmList";
import FirmFormModal from "../components/firms/FirmFormModal";
import PageLayout from "../components/PageLayout";

import { Firm, FirmFormData, FirmPageProps } from "../components/common/types";
import { addNewFirm, deleteFirm, editFirm, getAllFirms, FirmServerFilters } from "../api/firm";
import { PaginationMeta } from "../api/doctor";
import { useAuth } from "../context/AuthContext";


const exportToCsv = (filename: string, data: Firm[]): void => {
    // const headers = [
    //   'Doctor Code', 'Doctor Name', 'City', 'Hospital Name', 'Employee',
    //   'Specialty', 'Qualification', 'Category', 'Zone', 'Date of Birth',
    //   'Address', 'Created Date'
    // ];

    // const csvContent = [
    //   headers.join(','),
    //   ...data.map((doctor: Doctor) => [
    //     doctor.code,
    //     doctor.name,
    //     doctor.city || '',
    //     doctor.hospital_name || '',
    //     doctor.employee_id ? mockEmployees.find(e => e.id === doctor.employee_id)?.name || '' : '',
    //     getSpecialtyName(doctor.specialization_id),
    //     doctor.qualification || '',
    //     getCategoryName(doctor.doctor_category_id),
    //     doctor.zone_id ? mockZones.find(z => z.id === doctor.zone_id)?.name || '' : '',
    //     doctor.dob ? new Date(doctor.dob).toLocaleDateString() : '',
    //     doctor.address || '',
    //     // doctor.created_date ? new Date(doctor.created_date).toLocaleDateString() : ''
    //   ].map(field => `"${field?.toString().replace(/"/g, '""') || ''}"`).join(','))
    // ].join('\n');

    // const blob = new Blob([csvContent], { type: 'text/csv' });
    // const url = window.URL.createObjectURL(blob);
    // const a = document.createElement('a');
    // a.href = url;
    // a.download = filename;
    // a.click();
    // window.URL.revokeObjectURL(url);
};

const FirmPage: React.FC<FirmPageProps> = ({ onLogout, onNavigate }) => {
    const { user } = useAuth();
    const [firms, setFirms] = useState<Firm[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingFirm, setEditingFirm] = useState<Firm | null>(null);
    const [pagination, setPagination] = useState<PaginationMeta>({ current_page: 1, last_page: 1, per_page: 15, total: 0 });
    const [serverFilters, setServerFilters] = useState<FirmServerFilters>({});

    const loadFirms = useCallback(async (page: number = 1, filters: FirmServerFilters = {}) => {
        if (!user?.tenant_id) return;

        setIsLoading(true);
        try {
            const result = await getAllFirms(user.tenant_id, page, 15, filters);
            setFirms(result.data);
            setPagination(result.pagination);
        } catch (error) {
            console.error("Failed to load firms:", error);
        }
        setIsLoading(false);
    }, [user?.tenant_id]);

    const handleServerFilterChange = (filters: FirmServerFilters) => {
        setServerFilters(filters);
        loadFirms(1, filters);
    };



    useEffect(() => {
        if (user?.tenant_id) {
            loadFirms();
        }
    }, [user?.tenant_id, loadFirms]);


    const handleEdit = (firm: Firm): void => {
        setEditingFirm(firm);
        setIsEditModalOpen(true);
    };

    const handleDelete = async (firmId: number | string): Promise<void> => {
        try {
            console.log('Attempting to delete firm:', firmId);
            await deleteFirm(Number(firmId));
            await loadFirms();
        } catch (error) {
            console.error("Failed to delete firm:", error);
        }
    };

    const handleSubmit = async (firmData: FirmFormData): Promise<void> => {
        try {
            console.log(firmData);
            if (editingFirm) {
                const res = await editFirm(editingFirm.id!, firmData);
                if (res.status == 200) {
                    toast.success("Firm Updated Successfully!");
                }
            } else {
                const response = await addNewFirm(firmData);
                console.log("firm create res: ", response);
                if (response.status == 200) {
                    toast.success("Firm Added Successfully!");
                }
            }
            setIsAddModalOpen(false);
            setIsEditModalOpen(false);
            setEditingFirm(null);
            await loadFirms();
        } catch (error) {
            console.error("Failed to save firm:", error);
            toast.error("Something went wrong!");
        }
    };

    const handleExport = (): void => {
        exportToCsv("firms.csv", firms);
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const file = event.target.files?.[0];
        if (!file) return;

        console.log("File upload:", file.name);
        toast.info("CSV import functionality will be implemented with proper parsing");

        // Reset file input
        event.target.value = '';
    };

    return (
        <PageLayout onLogout={onLogout} currentPage="firms" onNavigate={onNavigate}>
            <div className="p-4 md:p-8">
                <div className="mx-auto max-w-7xl">
                    {/* Header */}
                    <div className="flex flex-col items-start justify-between gap-4 mb-8 md:flex-row md:items-center">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Firm</h1>
                            <p className="mt-1 text-blue-600">Manage your firm information</p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => document.getElementById('import-firms-input')?.click()}
                                className="flex items-center px-4 py-2 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                <Upload className="w-4 h-4 mr-2" />
                                Import
                            </button>
                            <input
                                type="file"
                                id="import-firms-input"
                                className="hidden"
                                onChange={handleFileUpload}
                                accept=".csv"
                            />
                            <button
                                onClick={handleExport}
                                className="flex items-center px-4 py-2 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Export
                            </button>
                            <button
                                onClick={() => setIsAddModalOpen(true)}
                                className="flex items-center px-4 py-2 text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add New Firm
                            </button>
                        </div>
                    </div>

                    {/* Firm List */}
                    <FirmList
                        firms={firms}
                        isLoading={isLoading}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        pagination={pagination}
                        onPageChange={(page) => loadFirms(page, serverFilters)}
                        serverFilters={serverFilters}
                        onServerFilterChange={handleServerFilterChange}
                    />

                    {/* Add Firm Modal */}
                    <FirmFormModal
                        isOpen={isAddModalOpen}
                        onOpenChange={setIsAddModalOpen}
                        onSubmit={handleSubmit}
                        title="Add New Firm"
                        submitButtonText="Add Firm"
                        onCancel={() => console.log("cancel")}
                    />

                    {/* Edit Firm Modal */}
                    <FirmFormModal
                        isOpen={isEditModalOpen}
                        onOpenChange={setIsEditModalOpen}
                        firm={editingFirm}
                        onSubmit={handleSubmit}
                        title="Edit Firm Information"
                        submitButtonText="Update Firm"
                        onCancel={() => console.log("cancel")}
                    />
                </div>
            </div>

        </PageLayout>
    );
};

export default FirmPage;